import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ApplicationForm.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    candidateName: '',
    age: '',
    gender: '',
    careerGoals: '',
    passportNumber: '',
    passportValidity: '',
    educationLevel: ''
  });

  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      navigate('/login');
      return;
    }

    // Pre-fill user's name
    try {
      const userData = JSON.parse(user);
      setFormData(prev => ({
        ...prev,
        candidateName: userData.name || ''
      }));
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    setFiles(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Validate required fields
    if (!formData.candidateName || !formData.age || !formData.gender || !formData.careerGoals || !formData.educationLevel) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Append form data
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      // Map file fields to backend expected names
      const fileMapping = {
        grade7: 'marksheet_7th',
        grade8: 'marksheet_8th',
        grade9: 'marksheet_9th',
        grade10: 'marksheet_10th',
        grade11: 'marksheet_11th',
        grade12: 'marksheet_12th',
        ugSem1: 'marksheet_sem1',
        ugSem2: 'marksheet_sem2',
        ugSem3: 'marksheet_sem3',
        ugSem4: 'marksheet_sem4',
        ugDegree: 'ug_degree',
        mastersDegree: 'masters_degree',
        phdDocs: 'phd_docs',
        fundsProof: 'fundsProof',
        passportCopy: 'passport',
        usVisaCopy: 'visa'
      };

      // Append files
      Object.keys(files).forEach(key => {
        if (files[key]) {
          const backendFieldName = fileMapping[key] || key;
          submitData.append(backendFieldName, files[key]);
        }
      });

      // Don't set Content-Type header - let browser set it automatically with boundary
      const response = await api.post('/application/submit', submitData);

      if (response.data.success) {
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(
        error.response?.data?.message || 
        'Failed to submit application. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderEducationUploaders = () => {
    switch(formData.educationLevel) {
      case 'high-school':
        return (
          <div className="education-uploaders">
            <h3 className="uploader-section-title">Upload Marksheets</h3>
            <div className="uploaders-grid">
              <FileUploader label="7th Grade Marksheet" name="grade7" onChange={handleFileChange} file={files.grade7} />
              <FileUploader label="8th Grade Marksheet" name="grade8" onChange={handleFileChange} file={files.grade8} />
              <FileUploader label="9th Grade Marksheet" name="grade9" onChange={handleFileChange} file={files.grade9} />
              <FileUploader label="10th Grade Marksheet" name="grade10" onChange={handleFileChange} file={files.grade10} />
            </div>
          </div>
        );
      
      case '10+2':
        return (
          <div className="education-uploaders">
            <h3 className="uploader-section-title">Upload Marksheets</h3>
            <div className="uploaders-grid">
              <FileUploader label="9th Grade Marksheet" name="grade9" onChange={handleFileChange} file={files.grade9} />
              <FileUploader label="10th Grade Marksheet" name="grade10" onChange={handleFileChange} file={files.grade10} />
              <FileUploader label="11th Grade Marksheet" name="grade11" onChange={handleFileChange} file={files.grade11} />
              <FileUploader label="12th Grade Marksheet" name="grade12" onChange={handleFileChange} file={files.grade12} />
            </div>
          </div>
        );
      
      case 'undergraduate':
        return (
          <div className="education-uploaders">
            <h3 className="uploader-section-title">Upload Documents</h3>
            <div className="uploaders-grid">
              <FileUploader label="10th Grade Marksheet" name="grade10" onChange={handleFileChange} file={files.grade10} />
              <FileUploader label="12th Grade Marksheet" name="grade12" onChange={handleFileChange} file={files.grade12} />
              <FileUploader label="Semester 1 Marksheet" name="ugSem1" onChange={handleFileChange} file={files.ugSem1} />
              <FileUploader label="Semester 2 Marksheet" name="ugSem2" onChange={handleFileChange} file={files.ugSem2} />
              <FileUploader label="Semester 3 Marksheet" name="ugSem3" onChange={handleFileChange} file={files.ugSem3} />
              <FileUploader label="Semester 4 Marksheet" name="ugSem4" onChange={handleFileChange} file={files.ugSem4} />
            </div>
          </div>
        );
      
      case 'masters':
        return (
          <div className="education-uploaders">
            <h3 className="uploader-section-title">Upload Documents</h3>
            <div className="uploaders-grid">
              <FileUploader label="10th Grade Marksheet" name="grade10" onChange={handleFileChange} file={files.grade10} />
              <FileUploader label="12th Grade Marksheet" name="grade12" onChange={handleFileChange} file={files.grade12} />
              <FileUploader label="Undergraduate Degree" name="ugDegree" onChange={handleFileChange} file={files.ugDegree} />
              <FileUploader label="Masters Semester 1" name="mastersSem1" onChange={handleFileChange} file={files.mastersSem1} />
              <FileUploader label="Masters Semester 2" name="mastersSem2" onChange={handleFileChange} file={files.mastersSem2} />
            </div>
          </div>
        );
      
      case 'phd':
        return (
          <div className="education-uploaders">
            <h3 className="uploader-section-title">Upload Documents</h3>
            <div className="uploaders-grid">
              <FileUploader label="10th Grade Marksheet" name="grade10" onChange={handleFileChange} file={files.grade10} />
              <FileUploader label="12th Grade Marksheet" name="grade12" onChange={handleFileChange} file={files.grade12} />
              <FileUploader label="Undergraduate Degree" name="ugDegree" onChange={handleFileChange} file={files.ugDegree} />
              <FileUploader label="Masters Degree" name="mastersDegree" onChange={handleFileChange} file={files.mastersDegree} />
              <FileUploader label="PhD Documents" name="phdDocs" onChange={handleFileChange} file={files.phdDocs} />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="application-form-page">
      <Header />
      
      <section className="application-form-section">
        <div className="container">
          <div className="form-header">
            <h3 className="form-subtitle">START YOUR JOURNEY</h3>
            <h1 className="form-title">APPLICATION <span className="title-highlight">FORM</span></h1>
            <p className="form-description">
              Complete the form below to begin your pilot training journey with Aviator1903.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="main-application-form">
            {errorMessage && (
              <div className="error-message" style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                {errorMessage}
              </div>
            )}

            <div className="form-section">
              <h2 className="section-title">Personal Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="candidateName">Candidate Name *</label>
                  <input
                    type="text"
                    id="candidateName"
                    name="candidateName"
                    placeholder="Enter your full name"
                    value={formData.candidateName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="age">Age *</label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    placeholder="Enter your age"
                    value={formData.age}
                    onChange={handleChange}
                    required
                    min="17"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender *</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="careerGoals">Career Goals *</label>
                  <textarea
                    id="careerGoals"
                    name="careerGoals"
                    placeholder="Describe your career goals and aspirations"
                    value={formData.careerGoals}
                    onChange={handleChange}
                    required
                    rows="4"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">Passport Details</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="passportNumber">Passport Number *</label>
                  <input
                    type="text"
                    id="passportNumber"
                    name="passportNumber"
                    placeholder="Enter passport number"
                    value={formData.passportNumber}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="passportValidity">Passport Validity Date *</label>
                  <input
                    type="date"
                    id="passportValidity"
                    name="passportValidity"
                    value={formData.passportValidity}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">Education Details</h2>
              <div className="form-group">
                <label htmlFor="educationLevel">Education Level *</label>
                <select
                  id="educationLevel"
                  name="educationLevel"
                  value={formData.educationLevel}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Education Level</option>
                  <option value="high-school">High School</option>
                  <option value="10+2">10+2</option>
                  <option value="undergraduate">Undergraduate</option>
                  <option value="masters">Masters</option>
                  <option value="phd">PhD</option>
                </select>
              </div>

              {renderEducationUploaders()}
            </div>

            <div className="form-section">
              <h2 className="section-title">Additional Documents (Optional)</h2>
              <div className="uploaders-grid">
                <FileUploader 
                  label="Passport Copy (If Applicable)" 
                  name="passportCopy" 
                  onChange={handleFileChange} 
                  file={files.passportCopy}
                  optional
                />
                <FileUploader 
                  label="US Visa Copy (If Applicable)" 
                  name="usVisaCopy" 
                  onChange={handleFileChange} 
                  file={files.usVisaCopy}
                  optional
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
                {!loading && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {showSuccess && (
        <div className="modal-overlay" onClick={() => {}}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/>
                <path d="M8 12l2 2 4-4" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Application Submitted Successfully!</h2>
            <p>
              Thank you for completing your application. Our team will review it and update your application status soon.
            </p>
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              padding: '15px',
              margin: '20px 0',
              textAlign: 'left'
            }}>
              <h3 style={{color: '#f59e0b', fontSize: '16px', marginBottom: '10px'}}>
                ✅ What's Next?
              </h3>
              <p style={{fontSize: '14px', lineHeight: '1.6', margin: '0'}}>
                You can now track your application status from your dashboard. 
                You'll receive email notifications for any status updates.
              </p>
            </div>
            <button 
              onClick={() => {
                navigate('/dashboard');
              }}
              className="btn-submit"
              style={{width: '100%'}}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

const FileUploader = ({ label, name, onChange, file, optional }) => {
  return (
    <div className="file-uploader">
      <label className="file-label">
        {label} {!optional && '*'}
      </label>
      <div className="file-input-wrapper">
        <input
          type="file"
          id={name}
          name={name}
          onChange={(e) => onChange(e, name)}
          accept=".pdf,.jpg,.jpeg,.png"
          required={!optional}
        />
        <label htmlFor={name} className="file-input-label">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {file ? file.name : 'Choose File'}
        </label>
      </div>
      {file && (
        <div className="file-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{file.name}</span>
        </div>
      )}
    </div>
  );
};

export default ApplicationForm;
